import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { ConditionalMenuContainer } from '../ConditionalMenuContainer/ConditionalMenuContainer';
import { CurrentOpenMenu } from '@/pages/SearchPageSimplified/types';
import { FacetOption } from '@/types/FacetOption';
import { FilterAction } from '@/types/SearchFacet';
import { FormEvent, MouseEvent, useMemo } from 'react';
import { getMergedOptionFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import { getRoundedNumberText } from '@/pages/SearchPageSimplified/utils/getRoundedNumberText';
import {
    modifyParamsRemoveFacet,
    modifyParamsRemovePage,
    modifyParamsWhenChecked,
    parseQueryParams,
} from '@/utils/queryParams';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';
import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import FacetTypes from '@/types/search/enums/facetTypes';

type Props = {
    currentOpenMenu?: CurrentOpenMenu;
    isAllFilters?: boolean;
    isArchivedSearch: boolean;
    onMenuClose: () => void;
    onMenuOpen: (currentMenu: CurrentOpenMenu) => void;
};

export const ShippingFilter = ({ currentOpenMenu, isAllFilters, isArchivedSearch, onMenuClose, onMenuOpen }: Props) => {
    const { formatMessage } = useIntl();
    const { search } = useLocation();

    const queryParams = parseQueryParams(search);
    const shippingFacet = useAppSelector((state) => getMergedOptionFacet(state, false, FacetTypes.SHIPPING));

    const navigateWithParams = useNavigateSearchPageWithParams();

    const onShippingOptionClick = (event: FormEvent<HTMLInputElement>, option?: FacetOption) => {
        event.stopPropagation();
        const newCheckStatus = event.currentTarget.checked;

        let newParams = modifyParamsWhenChecked(queryParams, FacetTypes.SHIPPING, option.id, newCheckStatus);
        newParams = modifyParamsRemovePage(newParams);
        navigateWithParams({
            checkForPageRemoval: true,
            currentOpenMenu: isAllFilters ? CurrentOpenMenu.AllFilters : CurrentOpenMenu.Shipping,
            filterAction: newCheckStatus ? FilterAction.ShippingChanged : FilterAction.ShippingRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    };

    const selectedShippingFacets: FacetOption[] = useMemo(() => {
        const selectedShippingOptions = shippingFacet?.options?.filter((option) => option.selected);

        return selectedShippingOptions ?? [];
    }, [shippingFacet?.options]);

    const onRemoveAllShippingOptions = (event: MouseEvent<HTMLInputElement>) => {
        event.preventDefault();

        let newParams = modifyParamsRemoveFacet(queryParams, FacetTypes.SHIPPING);
        newParams = modifyParamsRemovePage(newParams);

        navigateWithParams({
            checkForPageRemoval: true,
            filterAction: FilterAction.ShippingRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    };

    // Don't show shipping options for archived search
    if (isArchivedSearch) {
        return null;
    }

    return (
        <ConditionalMenuContainer
            accordionDefaultOpen
            currentOpenMenu={currentOpenMenu}
            currentOpenMenuId={CurrentOpenMenu.Shipping}
            isAllFilters={isAllFilters}
            onMenuClose={onMenuClose}
            onMenuOpen={onMenuOpen}
            title={formatMessage({ id: 'searchPage.facets.shipping' })}
            trigger={
                <Chip
                    onClose={onRemoveAllShippingOptions}
                    selected={Boolean(selectedShippingFacets?.length)}
                    style="singleFilter"
                >
                    {Boolean(selectedShippingFacets.length)
                        ? selectedShippingFacets.map((option) => option.name).join(', ')
                        : formatMessage({ id: 'searchPage.facets.shipping' })}
                </Chip>
            }
        >
            {shippingFacet?.options?.map((option) => (
                <Checkbox.Option
                    checked={option.selected}
                    disabled={option.count === 0}
                    key={option.id}
                    onChange={(event) => onShippingOptionClick(event, option)}
                >
                    {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                </Checkbox.Option>
            ))}
        </ConditionalMenuContainer>
    );
};
